import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  MapPin,
  Phone,
  Mail,
  ArrowUpRight
} from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { OPPORTUNITIES, findOpportunity } from "../data/opportunities";

export default function EnquiryPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const type = params.get("type") || "";
  const optionId = params.get("option") || "";

  const ctx = useMemo(() => findOpportunity(type, optionId), [type, optionId]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    location: "",
    message: ""
  });

  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  /* SCROLL TO TOP WHEN PAGE LOADS OR URL CHANGES */
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant"
    });
  }, [location.pathname, location.search]);

  /* AUTO PREFILL MESSAGE */
  useEffect(() => {
    if (ctx) {
      setForm((p) => ({
        ...p,
        message: `Interested in ${ctx.group.label} - ${ctx.option.title}. Please share details.`
      }));
    }
  }, [ctx]);

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill required fields.");
      return;
    }

    setLoading(true);

    try {
      await fetch(
        "https://script.google.com/macros/s/AKfycbzTY2BdmaDlI95yYcDCCcwzjQAxvOu3sVUrZ55kXZsDhzaXt19Oop0ZTE31_ctUoWxg/exec",
        {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...form,
            submitted_at: new Date().toISOString()
          })
        }
      );

      toast.success("Enquiry submitted successfully");
      setDone(true);
    } catch (err) {
      toast.error("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05070A] text-white">
      <Header />

      <main className="pt-28 pb-20 relative overflow-hidden">
        {/* TRICOLOR BACKGROUND GLOW */}
        <div className="absolute top-20 left-[-10%] w-[500px] h-[500px] bg-orange-500/10 blur-[140px] rounded-full" />
        <div className="absolute bottom-10 right-[-10%] w-[500px] h-[500px] bg-green-500/10 blur-[140px] rounded-full" />
        <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-white/5 blur-[160px] rounded-full -translate-x-1/2 -translate-y-1/2" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* BACK BUTTON */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/40 hover:text-white mb-10 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {/* TITLE */}
          <div className="mb-10">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              <span className="text-saffron">Partner</span>{" "}
              <span className="text-white">Enquiry</span>{" "}
              <span className="text-green-500">Portal</span>
            </h1>

            <p className="text-white/50 mt-3 max-w-xl">
              Submit your details to explore investment and franchise
              opportunities across India’s clean energy network.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* LEFT PANEL */}
            <div className="space-y-6">
              {!ctx ? (
                <div className="space-y-4">
                  {Object.values(OPPORTUNITIES).map((g) => (
                    <div key={g.id}>
                      <div className="text-xs text-white/40 mb-2 tracking-widest uppercase">
                        {g.label}
                      </div>

                      <div className="space-y-3">
                        {g.options.map((opt) => (
                          <button
                            key={opt.id}
                            onClick={() =>
                              navigate(
                                `/enquiry?type=${g.id}&option=${opt.id}`
                              )
                            }
                            className="w-full text-left p-5 rounded-xl border border-white/10 bg-white/5 hover:border-orange-400/40 hover:bg-white/10 transition"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-semibold text-white">
                                  {opt.title}
                                </div>
                                <div className="text-xs text-white/40">
                                  {opt.subtitle}
                                </div>
                              </div>

                              <ArrowUpRight className="w-4 h-4 text-white/40" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 rounded-xl border border-white/10 bg-white/5">
                  <div className="text-sm text-white/40">
                    Selected Category
                  </div>
                  <div className="text-lg font-semibold text-white mt-1">
                    {ctx.group.label}
                  </div>
                  <div className="text-green-400 mt-1">
                    {ctx.option.title}
                  </div>
                </div>
              )}

              {/* CONTACT SECTION */}
              <div className="pt-6 border-t border-white/10 space-y-4 text-white/60 text-sm">
                {/* ADDRESS */}
                <a
                  href="https://maps.google.com/?q=SCO+15+HSVP+Complex+Sector+4-5+Karnal+132001"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 hover:text-white transition"
                >
                  <MapPin className="w-4 h-4 text-saffron shrink-0 mt-1" />
                  <span>
                    SCO 15, HSVP Complex, Sector 4-5, Karnal 132001
                  </span>
                </a>

                {/* PHONE */}
                <a
                  href="tel:+918930225999"
                  className="flex items-center gap-3 hover:text-white transition"
                >
                  <Phone className="w-4 h-4 text-green-500 shrink-0" />
                  <span>+91 8930225999</span>
                </a>

                {/* EMAIL */}
                <a
                  href="mailto:contact@indianecofuels.co"
                  className="flex items-center gap-3 hover:text-white transition"
                >
                  <Mail className="w-4 h-4 text-white shrink-0" />
                  <span>contact@indianecofuels.co</span>
                </a>
              </div>
            </div>

            {/* RIGHT PANEL */}
            <div className="p-6 rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-white/0 backdrop-blur-xl">
              {done ? (
                <div className="text-center py-20">
                  <CheckCircle2 className="mx-auto text-green-400 w-12 h-12" />
                  <h2 className="text-2xl font-bold mt-6">Submitted</h2>
                  <p className="text-white/40 mt-2">
                    We will contact you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <input
                    placeholder="Name *"
                    className="w-full p-3 rounded-lg bg-white/5 border border-white/10"
                    value={form.name}
                    onChange={update("name")}
                  />

                  <input
                    placeholder="Email *"
                    className="w-full p-3 rounded-lg bg-white/5 border border-white/10"
                    value={form.email}
                    onChange={update("email")}
                  />

                  <input
                    placeholder="Phone"
                    className="w-full p-3 rounded-lg bg-white/5 border border-white/10"
                    value={form.phone}
                    onChange={update("phone")}
                  />

                  <input
                    placeholder="Company"
                    className="w-full p-3 rounded-lg bg-white/5 border border-white/10"
                    value={form.company}
                    onChange={update("company")}
                  />

                  <input
                    placeholder="City"
                    className="w-full p-3 rounded-lg bg-white/5 border border-white/10"
                    value={form.location}
                    onChange={update("location")}
                  />

                  <textarea
                    placeholder="Message *"
                    className="w-full p-3 rounded-lg bg-white/5 border border-white/10 h-28"
                    value={form.message}
                    onChange={update("message")}
                  />

                  <button
                    disabled={loading}
                    className="w-full py-3 rounded-xl font-semibold text-black bg-gradient-to-r from-saffron via-white to-green-500 hover:scale-[1.01] transition"
                  >
                    {loading ? "Submitting..." : "Submit Enquiry"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}